import { Data, Effect } from 'effect';

// ── Redacted failure channel ─────────────────────────────────────────────────
// The typed error carries a static operation label only. The original cause
// rides a side WeakMap so the module-scope install at the bottom rethrows the
// exact platform error, keeping app-boot failure behavior unchanged.

export type BrowserCompatOperation = 'polyfill' | 'dom-guards';

export class BrowserCompatError extends Data.TaggedError('BrowserCompatError')<{
  readonly operation: BrowserCompatOperation;
}> {}

const browserCompatErrorCauses = new WeakMap<BrowserCompatError, unknown>();

function browserCompatFailure(
  operation: BrowserCompatOperation,
  cause: unknown,
): BrowserCompatError {
  const error = new BrowserCompatError({ operation });
  browserCompatErrorCauses.set(error, cause);
  return error;
}

export function browserCompatErrorCause(error: BrowserCompatError): unknown {
  return browserCompatErrorCauses.get(error) ?? error;
}

function restoreBrowserCompatError<A>(
  program: Effect.Effect<A, BrowserCompatError>,
): Effect.Effect<A, unknown> {
  return program.pipe(
    Effect.catch((error) => Effect.fail(browserCompatErrorCauses.get(error) ?? error)),
  );
}

function relativeIndex(index: number, length: number): number {
  const integer = Math.trunc(Number(index)) || 0;
  return integer < 0 ? length + integer : integer;
}

function installAtPolyfills(): void {
  if (!Array.prototype.at) {
    Object.defineProperty(Array.prototype, 'at', {
      configurable: true,
      writable: true,
      value<T>(this: ArrayLike<T>, index: number): T | undefined {
        const length = this.length >>> 0;
        const position = relativeIndex(index, length);
        return position >= 0 && position < length ? this[position] : undefined;
      },
    });
  }

  if (!String.prototype.at) {
    Object.defineProperty(String.prototype, 'at', {
      configurable: true,
      writable: true,
      value(this: string, index: number): string | undefined {
        const value = String(this);
        const position = relativeIndex(index, value.length);
        return position >= 0 && position < value.length ? value.charAt(position) : undefined;
      },
    });
  }
}

// ── React × in-page-translator DOM-crash guard ───────────────────────────────
// Google Translate / Edge / Safari translators, and many extensions, "translate"
// a page by splitting and replacing the text nodes React manages. When React
// later commits an update — e.g. after the /tools file list mutates on a
// remove/reorder/clear click — it calls removeChild/insertBefore against nodes
// the translator already moved or detached, and the browser throws NotFoundError
// during the COMMIT phase. That surfaces as an `error_boundary_triggered` storm
// (the boundary catches it, re-renders, hits the same broken DOM, repeats) that
// white-screens the tool — observed on /tools/merge, 4× in ~15ms, 2026-07-09,
// despite the runner's `translate="no"` (translators honor that opt-out
// inconsistently). This is NOT a null-access in an event handler (those never
// reach an error boundary in React) — it is the reconciler's own DOM call.
//
// Fix the class at the source: make the two reconciler primitives tolerate a
// node that is no longer where React expects it, instead of throwing. Standard,
// framework-agnostic mitigation used across production React apps
// (facebook/react#11538). The tool ErrorBoundary stays as the last line; this
// stops the crash from firing in the first place, for every tool, not just merge.
function installTranslationDomGuards(): void {
  if (typeof Node === 'undefined' || !Node.prototype) return;
  const proto = Node.prototype;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    // A translator already detached/reparented the node — React's bookkeeping is
    // stale. Removing it again is a no-op; return the child so callers that read
    // the result keep working.
    if (child.parentNode !== this) return child;
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    // The reference node was reparented out from under React. Fall back to an
    // append (insertBefore(node, null)) so the new node still enters the DOM
    // rather than throwing NotFoundError mid-commit.
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

/** Native install program: `.at` polyfills first, then the DOM-crash guards. */
export const installBrowserCompatEffect = Effect.fn('browserCompat.install')(
  function* (): Effect.fn.Return<void, BrowserCompatError> {
    yield* Effect.try({
      try: () => installAtPolyfills(),
      catch: (cause) => browserCompatFailure('polyfill', cause),
    });
    yield* Effect.try({
      try: () => installTranslationDomGuards(),
      catch: (cause) => browserCompatFailure('dom-guards', cause),
    });
  },
);

// Runs synchronously at module scope, exactly where the inline installs used
// to run: main.tsx imports this module for its side effects before mounting,
// and a failed install must abort the boot with the original platform error.
Effect.runSync(restoreBrowserCompatError(installBrowserCompatEffect()));
